import { CreatorAction, DubDomain, OrgCreatorStats, OrgInboxStats } from "@/types/types";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { toast } from "sonner";
import { Usage } from "@/types/types";
interface DashboardState {
  // Data
  tasks: CreatorAction[];
  campaigns: any[];
  usage: Usage;
  dubDomains: DubDomain[];
  /** Org-wide creator funnel (Activated Creators dialog); not persisted. */
  creatorStats: OrgCreatorStats | null;
  /** Per-sending-inbox rollup (Campaigns dialog); not persisted. */
  inboxStats: OrgInboxStats[];

  // Loading states
  tasksLoading: boolean;
  campaignsLoading: boolean;
  usageLoading: boolean;
  dubDomainsLoading: boolean;
  creatorStatsLoading: boolean;

  // Actions
  fetchTasks: (organizationId: string, token: string) => Promise<void>;
  dismissHandoff: (campaignCreatorId: string, token: string) => Promise<boolean>;
  setCreatorStatus: (
    campaignCreatorId: string,
    status: string,
    opts: {
      promoCode?: string;
      manualUniqueLink?: string;
      selectedDomain?: string;
      useDub?: boolean;
      extraVariables?: Record<string, string>;
    },
    token: string
  ) => Promise<boolean>;
  fetchUsage: (token: string) => Promise<void>;
  fetchCampaigns: (organizationId: string, token: string) => Promise<void>;
  fetchDubDomains: (token: string) => Promise<void>;
  fetchCreatorStats: (organizationId: string, token: string) => Promise<void>;
  fetchAllData: (organizationId: string, token: string) => Promise<void>;

  // Reset
  resetStore: () => void;
}

// Cookie storage helper
const cookieStorage = {
  getItem: (name: string): string | null => {
    if (typeof document === "undefined") return null;
    const value = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`))
      ?.split("=")[1];
    return value ? decodeURIComponent(value) : null;
  },
  setItem: (name: string, value: string): void => {
    if (typeof document === "undefined") return;
    // Set cookie with 1 hour expiration
    const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; expires=${expires}; path=/; SameSite=Strict`;
  },
  removeItem: (name: string): void => {
    if (typeof document === "undefined") return;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  },
};

// Helper function to deep compare arrays
const arraysEqual = (a: any[], b: any[]): boolean => {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const defaultUsage: Usage = {
  activated_creators: { current: 0, limit: 10 },
  campaigns: { current: 0, limit: 1 },
  plan: "Free",
};

function usageEqual(a: Usage, b: Usage): boolean {
  return (
    a?.plan === b?.plan &&
    a?.activated_creators?.current === b?.activated_creators?.current &&
    a?.activated_creators?.limit === b?.activated_creators?.limit &&
    a?.campaigns?.current === b?.campaigns?.current &&
    a?.campaigns?.limit === b?.campaigns?.limit
  );
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      // Initial state
      tasks: [],
      usage: defaultUsage,
      campaigns: [],
      dubDomains: [],
      creatorStats: null,
      inboxStats: [],
      tasksLoading: false,
      campaignsLoading: false,
      usageLoading: false,
      dubDomainsLoading: false,
      creatorStatsLoading: false,

      fetchTasks: async (organizationId: string, token: string) => {
        const currentTasks = get().tasks;
        set({ tasksLoading: true });

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/campaigns/getUserTasks`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ organization_id: organizationId }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to fetch tasks");
          }

          const data = await response.json();
          if (data?.success === false) {
            console.error("Error fetching tasks:", data.message);
            set({ tasksLoading: false });
            return;
          }

          const newTasks = data?.tasks || [];

          // Set loading to false first so UI updates
          set({ tasksLoading: false });

          // Check if there are differences before updating cookie storage
          if (!arraysEqual(currentTasks, newTasks)) {
            set({ tasks: newTasks });
          }
        } catch (error) {
          console.error("Error fetching tasks:", error);
          set({ tasksLoading: false });
        }
      },

      dismissHandoff: async (campaignCreatorId: string, token: string) => {
        const snapshot = get().tasks;

        // Optimistic update: remove the handoff task from the list
        set((state) => ({
          tasks: state.tasks.filter(
            (task) =>
              !(task._id === campaignCreatorId && task.actionType === "handoff")
          ),
        }));

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/campaigns/dismissHandoff`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
            }
          );

          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || "Failed to dismiss handoff");
          }

          toast.success("Handoff dismissed & Autopilot turned ON");
          return true;
        } catch (error) {
          set({ tasks: snapshot }); // Rollback on error
          toast.error("Failed to dismiss handoff");
          console.error("Error dismissing handoff:", error);
          return false;
        }
      },

      setCreatorStatus: async (campaignCreatorId, status, opts, token) => {
        const snapshot = get().tasks;

        // Optimistic update: remove the task from the list
        set((state) => ({
          tasks: state.tasks.filter((task) => task._id !== campaignCreatorId),
        }));

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/campaigns/updateCreatorStatus`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                campaign_creator_id: campaignCreatorId,
                status,
                promo_code: opts.promoCode ?? "",
                manual_unique_link: opts.manualUniqueLink ?? "",
                selected_domain: opts.selectedDomain ?? "",
                use_dub: opts.useDub ?? false,
                extra_variables: opts.extraVariables ?? {},
              }),
            }
          );

          const data = await response.json();
          if (!response.ok || data.success === false) {
            throw new Error(data.error || "Failed to update creator status");
          }

          toast.success(
            status === "pending-activation"
              ? "Creator approved"
              : "Creator rejected"
          );
          return true;
        } catch (error) {
          set({ tasks: snapshot }); // Rollback on error
          toast.error("Failed to update creator status");
          console.error("Error updating creator status:", error);
          return false;
        }
      },

      fetchCampaigns: async (organizationId: string, token: string) => {
        const currentCampaigns = get().campaigns;
        set({ campaignsLoading: true });

        try {
          const response = await fetch(`${API_BASE_URL}/api/campaigns/getAll`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ organization_id: organizationId }),
          });

          if (!response.ok) {
            throw new Error("Failed to fetch campaigns");
          }

          const data = await response.json();
          // console.log("Campaigns data:", data);

          const newCampaigns = data.campaigns || [];

          // Set loading to false first so UI updates
          set({ campaignsLoading: false });

          // Check if there are differences before updating cookie storage
          if (!arraysEqual(currentCampaigns, newCampaigns)) {
            set({ campaigns: newCampaigns });
          }
        } catch (error) {
          console.error("Error fetching campaigns:", error);
          set({ campaignsLoading: false });
        }
      },

      fetchUsage: async (token: string) => {
        const currentUsage = get().usage;
        // Note: There's no usageLoading state defined, you may want to add one
        set({ usageLoading: true });

        try {
          const response = await fetch(`${API_BASE_URL}/api/usage/get`, {
            // Update endpoint
            method: "GET", // or POST if your API requires it
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            // Add body if needed: body: JSON.stringify({ /* params */ }),
          });

          if (!response.ok) {
            throw new Error("Failed to fetch usage");
          }

          const data = await response.json();

          const newUsage = data.usage || []; // Adjust based on API response structure
          // console.log("New usage data:", newUsage);
          // console.log("Current usage from store:", currentUsage);
          // console.log("Current usage:", data.usage);

          if (!usageEqual(currentUsage, newUsage)) {
            // console.log("Updating usage in store:", newUsage);
            set({ usage: newUsage });
          }
          set({ usageLoading: false });
        } catch (error) {
          console.error("Error fetching usage:", error);
          set({ usageLoading: false });
        }
      },

      fetchDubDomains: async (token: string) => {
        const currentDomains = get().dubDomains;
        set({ dubDomainsLoading: true });
        try {
          const response = await fetch(`${API_BASE_URL}/api/analytics/getDomains`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error("Failed to fetch Dub domains");
          }

          const data = await response.json();
          const newDomains = data.domains || [];

          set({ dubDomainsLoading: false });

          if (!arraysEqual(currentDomains, newDomains)) {
            set({ dubDomains: newDomains });
          }
        } catch (error) {
          console.error("Error fetching Dub domains:", error);
          set({ dubDomainsLoading: false });
        }
      },

      fetchCreatorStats: async (organizationId: string, token: string) => {
        // Cleared up front so an org switch never shows the previous org's
        // numbers while the new ones load.
        set({ creatorStatsLoading: true, creatorStats: null, inboxStats: [] });

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/campaigns/getOrgCreatorStats`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ organization_id: organizationId }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to fetch creator stats");
          }

          const data = await response.json();
          set({
            creatorStats: data?.success ? (data.stats ?? null) : null,
            inboxStats: data?.success ? (data.inboxes ?? []) : [],
            creatorStatsLoading: false,
          });
        } catch (error) {
          console.error("Error fetching creator stats:", error);
          set({ creatorStats: null, inboxStats: [], creatorStatsLoading: false });
        }
      },

      fetchAllData: async (organizationId: string, token: string) => {
        const { fetchTasks, fetchCampaigns, fetchUsage, fetchDubDomains } = get();
        await Promise.all([
          fetchTasks(organizationId, token),
          fetchCampaigns(organizationId, token),
          fetchUsage(token),
          fetchDubDomains(token),
        ]);
      },

      resetStore: () => {
        set({
          tasks: [],
          campaigns: [],
          usage: defaultUsage,
          dubDomains: [],
          creatorStats: null,
          inboxStats: [],
          tasksLoading: false,
          campaignsLoading: false,
          dubDomainsLoading: false,
          creatorStatsLoading: false,
        });
        // Clear cookies
        cookieStorage.removeItem("dashboard-store");
      },
    }),
    {
      name: "dashboard-store",
      storage: createJSONStorage(() => cookieStorage),
      partialize: (state) => ({
        tasks: state.tasks,
        usage: state.usage,
        campaigns: state.campaigns,
        dubDomains: state.dubDomains,
      }),
    }
  )
);
